// @ts-check

/**
 * The optional `form_success` parameters: which ones each form reports, and
 * the standardized English values they may carry.
 *
 * Kept free of dependencies so the runtime helper (`analytics.js`) can use the
 * value maps without bundling Zod; `analytics-schemas.js` builds the test-time
 * contract from the same data.
 * @see apps/docs/src/frontend/form-analytics.md
 */

/**
 * @typedef {object} TrackingParameterDefinition
 * @property {boolean} multiple - Reported as an array (checkbox group) rather
 *   than a single string (radio, select).
 * @property {Readonly<Record<string, string>>} values - Maps the value found
 *   in the form (the input's `data-track-value`, falling back to its `value`)
 *   to the standardized English value from Finstral's Data Layer
 *   specification. Anything not listed here is never reported.
 */

/**
 * The categorical `form_success` parameters and their standardized values,
 * as defined in the Data Layer specification attached to FINSTRAL-2211.
 *
 * The keys on the left are what our markup carries. Where an input submits a
 * translated label as its `value` (interest, frame material, day, time), the
 * machine key is rendered separately as `data-track-value`, so the reported
 * value never depends on the page language.
 *
 * Renaming a value on the right is a breaking change for GA4 segments and
 * Google Ads bidding. Coordinate it with Finstral's tracking team.
 * @type {Readonly<Record<string, TrackingParameterDefinition>>}
 */
export const TRACKING_PARAMETERS = Object.freeze({
	contact_type: {
		multiple: false,
		values: Object.freeze({
			"on-site-appointment": "onsite_appointment",
			"showroom-appointment": "showroom_appointment",
			"quote-request": "request_quote",
			message: "message",
			materials: "infomaterial",
		}),
	},
	topic: {
		multiple: true,
		values: Object.freeze({
			windows: "windows",
			doors: "front_doors",
			window_walls: "window_walls",
			window_replacement: "window_replacement",
			reconstruction: "reconstruction",
		}),
	},
	materials: {
		multiple: true,
		values: Object.freeze({
			pvc: "pvc",
			aluminium: "aluminum",
			wood: "wood",
			glass: "glass",
			ceramic: "ceramic",
			metal: "metal",
		}),
	},
	construction_type: {
		multiple: false,
		values: Object.freeze({
			"window-exchange": "window_replacement",
			renovation: "renovation",
			misc: "miscellaneous",
		}),
	},
	interested_in_doors: {
		multiple: false,
		values: Object.freeze({ yes: "yes", no: "no" }),
	},
	weekdays: {
		multiple: true,
		values: Object.freeze({
			monday: "monday",
			tuesday: "tuesday",
			wednesday: "wednesday",
			thursday: "thursday",
			friday: "friday",
			saturday: "saturday",
		}),
	},
	time_of_day: {
		multiple: true,
		values: Object.freeze({
			morning: "morning",
			mid_morning: "mid_morning",
			afternoon: "afternoon",
			evening: "evening",
		}),
	},
	additional_materials: {
		multiple: true,
		values: Object.freeze({
			window_check_kit: "check_kit",
			glass_walls_catalog: "catalog",
		}),
	},
	contact_role: {
		multiple: false,
		values: Object.freeze({
			designer: "designer",
			trade: "trade",
			private_client: "private_client",
			architect: "architect",
			student: "student",
		}),
	},
	magazine_language: {
		multiple: false,
		values: Object.freeze({
			"61-0804-00-01": "de",
			"61-0804-00-02": "it",
			"61-0804-00-07": "en",
			"61-0804-00-03": "fr",
			"61-0804-00-04": "es",
			"61-0804-00-09": "nl",
			"61-0804-00-15": "nl_be",
		}),
	},
});

/** Parameters every form with the shared privacy section reports. */
const CONSENT_PARAMETERS = ["marketing_consent", "partner_consent"];

/** Parameters every redirecting form reports. */
const DESTINATION_PARAMETERS = ["form_destination"];

/**
 * The optional parameters each form may report, beyond `form_id`,
 * `form_name` and `lead_type`. A parameter a form is not listed for fails the contract, so a
 * new field has to be added here on purpose.
 * @type {Readonly<Record<string, readonly string[]>>}
 */
export const PARAMETERS_BY_FORM = Object.freeze({
	"architect-talk-registration-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
	],
	"cad-data-request-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
		"product_id",
	],
	"check-kit-order-form": [...DESTINATION_PARAMETERS, ...CONSENT_PARAMETERS],
	"dealer-request-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_type",
		"topic",
		"weekdays",
		"time_of_day",
		"construction_type",
		"interested_in_doors",
		"materials",
		"partner_studio_kkz",
	],
	"f04-order-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
		"magazine_language",
	],
	"find-partner-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"topic",
		"additional_materials",
	],
	"information-request-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_type",
		"weekdays",
		"time_of_day",
		"additional_materials",
	],
	"magic-link-registration-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
		"terms_consent",
	],
	"magic-link-request-form": [...DESTINATION_PARAMETERS],
	"marketing-material-order-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"selected_brochures",
		"selected_sample_sets",
		"interest_category",
	],
	"partner-service-form": [...DESTINATION_PARAMETERS, ...CONSENT_PARAMETERS],
	"product-sample-request-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
		"product_id",
	],
	"request-tender-form": [
		...DESTINATION_PARAMETERS,
		...CONSENT_PARAMETERS,
		"contact_role",
		"product_id",
	],
	"service-form": [...DESTINATION_PARAMETERS, ...CONSENT_PARAMETERS],
});

/**
 * @typedef {object} FormType
 * @property {string} form_id   - Stable technical ID (`form_<snake_case>`).
 * @property {string} form_name - Human-readable English name.
 */

/**
 * A `FORM_TYPES` entry: one form type, or variant name → form type.
 * @typedef {FormType | Readonly<Record<string, FormType>>} FormTypeEntry
 */

/**
 * Whether a `FORM_TYPES` entry is a single form type rather than a map of
 * variants.
 * @param {FormTypeEntry} entry
 * @returns {entry is FormType}
 */
const isFormType = (entry) => typeof entry.form_id === "string";

/**
 * The form type each `form_success` event reports, as defined per form in
 * Finstral's Data Layer specification ("Data Layer 1.pdf" on FINSTRAL-2211).
 *
 * Keyed by component (the form's folder under `patterns/forms/`). A component
 * that serves several form types maps a variant to each:
 *
 * - `dealer-request-form`: the contact type preselected by the page (a hidden
 *   `contactType` input). Pages with the contact-type selector (store and
 *   studio pages), the Calendly booking and a preselected showroom
 *   appointment are all a Studio Contact (`default`).
 * - `marketing-material-order-form`: the editor's variant (`prospectus` or
 *   `sample`, from the embedded `additional-form-data`).
 *
 * `magic-link-request-form`, `architect-talk-registration-form` and
 * `find-partner-form` (FINSTRAL-2271) are not in the specification; their IDs
 * follow its format and were flagged to the tracking team for confirmation.
 *
 * Renaming an ID or name is a breaking change for GA4 and Google Ads.
 * @type {Readonly<Record<string, FormTypeEntry>>}
 */
export const FORM_TYPES = Object.freeze({
	"architect-talk-registration-form": {
		form_id: "form_event_registration",
		form_name: "Event Registration",
	},
	"cad-data-request-form": {
		form_id: "form_cad_data_request",
		form_name: "CAD Data Request",
	},
	"check-kit-order-form": {
		form_id: "form_window_check_kit",
		form_name: "Window Check Kit",
	},
	"dealer-request-form": Object.freeze({
		default: { form_id: "form_studio_contact", form_name: "Studio Contact" },
		"on-site-appointment": {
			form_id: "form_home_visit",
			form_name: "Schedule a Home Visit",
		},
		"quote-request": {
			form_id: "form_quote_request",
			form_name: "Request a Quote",
		},
		message: { form_id: "form_contact", form_name: "Contact Finstral" },
	}),
	"f04-order-form": { form_id: "form_magazine", form_name: "Magazine Order" },
	"find-partner-form": {
		form_id: "form_find_partner",
		form_name: "Find a Partner",
	},
	"information-request-form": {
		form_id: "form_lp",
		form_name: "Landing Page Lead",
	},
	"magic-link-registration-form": {
		form_id: "form_compendium_registration",
		form_name: "Compendium Registration",
	},
	"magic-link-request-form": {
		form_id: "form_compendium_access_request",
		form_name: "Compendium Access Request",
	},
	"marketing-material-order-form": Object.freeze({
		default: { form_id: "form_brochure_order", form_name: "Brochure Order" },
		sample: {
			form_id: "form_color_hand_samples",
			form_name: "Color and Hand Samples Request",
		},
	}),
	"partner-service-form": {
		form_id: "form_partner_service",
		form_name: "Partner Service Inquiry",
	},
	"product-sample-request-form": {
		form_id: "form_product_sample_request",
		form_name: "Product Sample Request",
	},
	"request-tender-form": {
		form_id: "form_tender_request",
		form_name: "Tender Text Request",
	},
	"service-form": {
		form_id: "form_service_request",
		form_name: "Service Request",
	},
});

/**
 * The form type a component reports, or `undefined` for an unknown component.
 * @param {string} component - The form's folder name, e.g. `dealer-request-form`.
 * @param {string} [variant] - The dealer form's preselected contact type, or
 *                             the marketing material form's variant. An
 *                             unknown or missing variant falls back to the
 *                             component's `default` type.
 * @returns {FormType | undefined}
 */
export const resolveFormType = (component, variant) => {
	const entry = /** @type {Record<string, FormTypeEntry | undefined>} */ (
		FORM_TYPES
	)[component];
	if (!entry) return undefined;
	if (isFormType(entry)) return entry;
	return (variant && entry[variant]) || entry.default;
};

/**
 * Every form type, flattened: the values `form_id` and `form_name` may take.
 * @type {readonly (FormType & { component: string })[]}
 */
export const ALL_FORM_TYPES = Object.freeze(
	Object.entries(FORM_TYPES).flatMap(([component, entry]) =>
		isFormType(entry)
			? [{ component, ...entry }]
			: Object.values(entry).map((type) => ({ component, ...type })),
	),
);
